import { useCallback, useEffect, useMemo, useState } from "react";
import { apiFetch } from "../../../config/api";
import { formatDate } from "../../../config/utils";
import EmptyState from "../../../components/EmptyState";
import Loader from "../../../components/Loader";
import PageHeader from "../../../components/PageHeader";
import Pagination from "../../../components/Pagination";
import { ClientPortalBadge } from "../clientPortalUtils";

const PAGE_SIZE = 10;
const EMPTY_FORM = {
  clientAccountId: "",
  projectId: "",
  fileName: "",
  documentType: "General",
  fileUrl: "",
  status: "Shared",
};

export default function ClientDocumentsPage() {
  const [documents, setDocuments] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [accountId, setAccountId] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const loadLookups = useCallback(async () => {
    try {
      const [accountsRes, projectsRes] = await Promise.all([
        apiFetch("/api/client-portal/accounts"),
        apiFetch("/api/projects"),
      ]);
      if (accountsRes.ok) setAccounts(await accountsRes.json());
      if (projectsRes.ok) setProjects(await projectsRes.json());
    } catch {
      setAccounts([]);
      setProjects([]);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      if (accountId) params.set("accountId", accountId);
      const res = await apiFetch(`/api/client-portal/documents?${params.toString()}`);
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setDocuments(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.message || "Could not load documents.");
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => { loadLookups(); }, [loadLookups]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [accountId]);

  const totalPages = Math.max(1, Math.ceil(documents.length / PAGE_SIZE));
  const pagedDocuments = useMemo(() => {
    const safePage = Math.min(page, totalPages);
    return documents.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  }, [documents, page, totalPages]);

  function updateField(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  function selectAccount(nextAccountId) {
    const account = accounts.find((item) => item.id === nextAccountId);
    setForm((current) => ({
      ...current,
      clientAccountId: nextAccountId,
      projectId: account?.linkedProjectId || current.projectId,
    }));
  }

  function openCreate() {
    setForm({
      ...EMPTY_FORM,
      clientAccountId: accountId || "",
    });
    setModalOpen(true);
  }

  async function createDocument(event) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = {
        ...form,
        projectId: form.projectId || null,
      };
      const res = await apiFetch("/api/client-portal/documents", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(await res.text());
      setModalOpen(false);
      await load();
    } catch (err) {
      setError(err?.message || "Could not share document.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="client-portal-page">
      <PageHeader
        title="Documents"
        subtitle={`${documents.length} document${documents.length === 1 ? "" : "s"} in the current view.`}
        actions={<button className="primary" onClick={openCreate}>Share Document</button>}
      />

      <div className="crm-toolbar glass-card" style={{ marginBottom: 16 }}>
        <div className="crm-search">
          <select value={accountId} onChange={(event) => setAccountId(event.target.value)} aria-label="Filter by client">
            <option value="">All clients</option>
            {accounts.map((account) => <option key={account.id} value={account.id}>{account.name}</option>)}
          </select>
        </div>
      </div>

      {error && <div className="error-banner" style={{ marginBottom: 16 }}>{error}</div>}

      <div className="glass-card client-portal-table-card">
        {loading ? (
          <div style={{ padding: 20 }}>
            {[1, 2, 3, 4, 5].map((item) => <Loader key={item} type="skeleton" height={48} style={{ marginBottom: 10 }} />)}
          </div>
        ) : documents.length === 0 ? (
          <EmptyState
            title="No documents shared"
            subtitle="Share agreements, reports, MoMs, project plans, and delivery files with clients."
            action={<button className="primary" onClick={openCreate}>Share Document</button>}
          />
        ) : (
          <>
            <div className="client-portal-table-wrap">
              <table className="client-portal-table">
                <thead>
                  <tr>
                    <th>Document</th>
                    <th>Client</th>
                    <th>Project</th>
                    <th>Shared By</th>
                    <th>Uploaded</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedDocuments.map((document) => (
                    <tr key={document.id}>
                      <td>
                        <div className="client-portal-row-title">{document.fileName}</div>
                        <div className="client-portal-row-subtitle">{document.documentType}</div>
                      </td>
                      <td>{document.clientName}</td>
                      <td>{document.projectName || "-"}</td>
                      <td>{document.sharedByUserName || "-"}</td>
                      <td>{formatDate(document.uploadedAt)}</td>
                      <td><ClientPortalBadge status={document.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={Math.min(page, totalPages)} totalPages={totalPages} onPageChange={setPage} total={documents.length} pageSize={PAGE_SIZE} />
          </>
        )}
      </div>

      {modalOpen && (
        <div className="modal-overlay" onClick={() => setModalOpen(false)}>
          <form className="glass-card modal crm-modal" onSubmit={createDocument} onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <h3>Share Document</h3>
              <button type="button" className="modal-close" onClick={() => setModalOpen(false)} aria-label="Close">x</button>
            </div>
            <div className="crm-form-grid">
              <label>
                Client
                <select value={form.clientAccountId} onChange={(event) => selectAccount(event.target.value)} required>
                  <option value="">Select client</option>
                  {accounts.map((account) => <option key={account.id} value={account.id}>{account.name}</option>)}
                </select>
              </label>
              <label>
                Project
                <select value={form.projectId} onChange={(event) => updateField("projectId", event.target.value)}>
                  <option value="">Use client default</option>
                  {projects.map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}
                </select>
              </label>
              <label>
                File Name
                <input value={form.fileName} onChange={(event) => updateField("fileName", event.target.value)} required />
              </label>
              <label>
                Document Type
                <input value={form.documentType} onChange={(event) => updateField("documentType", event.target.value)} />
              </label>
              <label>
                File URL
                <input value={form.fileUrl} onChange={(event) => updateField("fileUrl", event.target.value)} placeholder="/documents/file.pdf" />
              </label>
              <label>
                Status
                <select value={form.status} onChange={(event) => updateField("status", event.target.value)}>
                  <option>Shared</option>
                  <option>Archived</option>
                </select>
              </label>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn-ghost" onClick={() => setModalOpen(false)}>Cancel</button>
              <button type="submit" className="primary" disabled={saving}>{saving ? "Saving..." : "Share Document"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
